// Firebase Authentication utilities
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from './config';
import { doc, setDoc, getDoc, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from './config';

/**
 * Sign up a new user
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - Optional display name
 * @param {Object} consent - Consent preferences and phone number
 * @param {boolean} consent.emailConsent - Email notifications consent
 * @param {boolean} consent.privacyConsent - Privacy policy consent
 * @param {boolean} consent.smsConsent - SMS notifications consent
 * @param {string|null} consent.phoneNumber - Phone number in E.164 format
 */
export const signUp = async (email, password, displayName, consent = {}) => {
  if (!auth || !db) {
    throw new Error('Firebase is not configured. Please set up environment variables.');
  }
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    if (displayName) {
      await updateProfile(user, { displayName });
    }

    const now = new Date().toISOString();

    // Look up invite to get sport and persona if inviteToken is present
    // Also use sportFromUrl as fallback (passed from invite landing page)
    let sport = consent.sportFromUrl || 'bjj'; // Use URL sport or default to BJJ
    let invitePersonaId = null;
    if (consent.inviteToken) {
      try {
        const inviteDoc = await getDoc(doc(db, 'invites', consent.inviteToken));
        if (inviteDoc.exists()) {
          const inviteData = inviteDoc.data();
          sport = inviteData.sport || 'bjj';
          invitePersonaId = inviteData.personaId || null;
          
          // Mark invite as converted (user signed up)
          try {
            await updateDoc(doc(db, 'invites', consent.inviteToken), {
              converted: true,
              convertedAt: now,
              convertedUserId: user.uid,
              status: 'converted'
            });
          } catch (conversionError) {
            console.warn('Could not mark invite as converted:', conversionError);
          }
        }
      } catch (e) {
        console.warn('Could not look up invite:', e);
      }
    }

    // Create user and profile documents atomically using batch write
    const batch = writeBatch(db);
    const userRef = doc(db, 'users', user.uid);
    const profileRef = doc(db, 'profiles', user.uid);

    // Determine if user has phone and SMS consent
    const hasPhone = !!consent.phoneNumber;
    const hasSmsConsent = hasPhone && consent.smsConsent;

    // User document with consent tracking
    batch.set(userRef, {
      email: user.email,
      displayName: displayName || user.displayName || '',
      createdAt: now,
      phoneNumber: consent.phoneNumber || null,
      inviteToken: consent.inviteToken || null,
      sport,
      invitePersonaId,
      preferences: {
        emailNotifications: consent.emailConsent || false,
        smsReminders: hasSmsConsent,
      },
      consent: {
        privacyPolicy: {
          agreed: consent.privacyConsent || false,
          timestamp: consent.privacyConsent ? now : null,
        },
        emailNotifications: {
          agreed: consent.emailConsent || false,
          timestamp: consent.emailConsent ? now : null,
        },
        smsNotifications: {
          agreed: hasSmsConsent,
          timestamp: hasSmsConsent ? now : null,
        },
      },
    });

    // Profile document for Dashboard
    batch.set(profileRef, {
      userId: user.uid,
      email: user.email,
      displayName: displayName || user.displayName || '',
      createdAt: now,
      phoneNumber: consent.phoneNumber || null,
      inviteToken: consent.inviteToken || null,
      sport,
      invitePersonaId,
      // If phone is provided with SMS consent, skip onboarding phone step
      onboardingCompleted: hasSmsConsent,
      preferences: {
        emailNotifications: consent.emailConsent || false,
        smsReminders: hasSmsConsent,
      },
      consent: {
        smsNotifications: {
          agreed: hasSmsConsent,
          timestamp: hasSmsConsent ? now : null,
        },
      },
      // Terms acceptance for legal compliance
      termsAccepted: {
        version: '1.0',
        acceptedAt: now,
        disclaimersAcknowledged: {
          notMedicalAdvice: true,
          notMentalHealthService: true,
          userResponsibility: true,
          aiMayHaveErrors: true,
        },
      },
    });

    // Commit both writes atomically
    await batch.commit();

    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Sign in an existing user
 */
export const signIn = async (email, password) => {
  if (!auth) {
    throw new Error('Firebase is not configured. Please set up environment variables.');
  }
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Sign out the current user
 */
export const logOut = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Subscribe to auth state changes
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get user profile from Firestore
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { profile: userDoc.data(), error: null };
    }
    return { profile: null, error: 'User profile not found' };
  } catch (error) {
    return { profile: null, error: error.message };
  }
};

/**
 * Sign in with Google
 * @param {string} inviteToken - Optional invite token to link account
 * @param {string} sportFromUrl - Optional sport from URL params
 */
export const signInWithGoogle = async (inviteToken = null, sportFromUrl = null) => {
  if (!auth || !db) {
    throw new Error('Firebase is not configured. Please set up environment variables.');
  }

  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if user document exists
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    // If new user, create user and profile documents atomically
    // If existing user with invite, update sport from invite
    const now = new Date().toISOString();

    // Look up invite to get sport and persona if inviteToken is present
    // Also use sportFromUrl as fallback (passed from invite landing page)
    let sport = sportFromUrl || 'bjj'; // Use URL sport or default to BJJ
    let invitePersonaId = null;
    if (inviteToken) {
      try {
        const inviteDoc = await getDoc(doc(db, 'invites', inviteToken));
        if (inviteDoc.exists()) {
          const inviteData = inviteDoc.data();
          sport = inviteData.sport || 'bjj';
          invitePersonaId = inviteData.personaId || null;
          
          // Mark invite as converted (user signed up)
          try {
            await updateDoc(doc(db, 'invites', inviteToken), {
              converted: true,
              convertedAt: now,
              convertedUserId: user.uid,
              status: 'converted'
            });
          } catch (conversionError) {
            console.warn('Could not mark invite as converted:', conversionError);
          }
        }
      } catch (e) {
        console.warn('Could not look up invite:', e);
      }
    }

    if (!userDoc.exists()) {

      // Use batch write for atomic creation of both documents
      const batch = writeBatch(db);
      const profileRef = doc(db, 'profiles', user.uid);

      batch.set(userDocRef, {
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || null,
        createdAt: now,
        phoneNumber: null,
        authProvider: 'google',
        inviteToken: inviteToken || null,
        sport,
        invitePersonaId,
        preferences: {
          emailNotifications: false,
          smsReminders: false,
        },
        consent: {
          privacyPolicy: {
            agreed: true, // Assumed for Google sign-in
            timestamp: now,
          },
          emailNotifications: {
            agreed: false,
            timestamp: null,
          },
          smsNotifications: {
            agreed: false,
            timestamp: null,
          },
        },
      });

      batch.set(profileRef, {
        userId: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || null,
        createdAt: now,
        phoneNumber: null,
        authProvider: 'google',
        inviteToken: inviteToken || null,
        sport,
        invitePersonaId,
        onboardingCompleted: false,
        preferences: {
          emailNotifications: false,
          smsReminders: false,
        },
      });

      // Commit both writes atomically
      await batch.commit();
    } else {
      // Existing user - if they have an inviteToken, update sport from invite
      if (inviteToken && sport !== 'bjj') {
        try {
          const batch = writeBatch(db);
          const userDocRef = doc(db, 'users', user.uid);
          const profileRef = doc(db, 'profiles', user.uid);
          
          // Update both collections with sport from invite
          batch.update(userDocRef, {
            sport,
            inviteToken: inviteToken || null,
            invitePersonaId: invitePersonaId || null,
            updatedAt: now,
          });
          
          batch.update(profileRef, {
            sport,
            inviteToken: inviteToken || null,
            invitePersonaId: invitePersonaId || null,
            updatedAt: now,
          });
          
          await batch.commit();
          console.log(`Updated existing user ${user.uid} with sport ${sport} from invite`);
        } catch (updateError) {
          console.warn('Could not update existing user sport from invite:', updateError);
        }
      }
    }

    return { user, error: null };
  } catch (error) {
    console.error('Google sign-in error:', error);
    return { user: null, error: error.message };
  }
};

